import { useState } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconCalendarEvent } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { CalendarSyncSetupState } from '../lib/api/domains/calendar';
import { useAuthStore } from '../stores/auth';
import { CALENDAR_SYNC_SETUP_PATH } from './calendar/calendarSyncSetupLink';

const DISMISS_KEY = 'calendar-outlook-setup-banner-dismissed';

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/** Was fehlt noch — in der Reihenfolge, in der es sich beheben lässt. */
function missingStep(state: CalendarSyncSetupState): string {
  if (!state.microsoftConfigured) {
    return 'Dafür braucht es zuerst die Microsoft-Anmeldung des Portals (gleiche Seite, per Auto-Setup).';
  }
  if (!state.secretConfigured) {
    return 'Dafür fehlt der Portal-App noch das Client-Secret (gleiche Seite, bei der Microsoft-Anmeldung) — ohne das gibt es keine Firmen-Berechtigung.';
  }
  return 'Einmal einrichten, dann bei allen: Anmeldung per Gerätecode mit einem globalen Administrator oder Administrator für privilegierte Rollen.';
}

function reachSentence(state: CalendarSyncSetupState): string {
  const { delegatedConnected: n, activeUsers: total } = state;
  if (n === 0) return 'Bis dahin stehen Team-Termine bei niemandem in Outlook, nur im Portal.';
  const who = n === 1 ? 'einem' : String(n);
  return `Bis dahin stehen Team-Termine nur bei ${who} von ${total} Mitarbeitern in Outlook — bei denen, die ihr Outlook im Kalender selbst verbunden haben.`;
}

/**
 * Admin-Hinweis: Die Firmen-Berechtigung für den Teamkalender (Outlook) fehlt noch.
 *
 * Ohne sie landen Team-Termine nur im Outlook derer, die sich selbst verbunden haben — alle anderen
 * erfahren von einem Jour fixe nur im Portal. Einrichten kann es nur ein Admin, deshalb hier, mit
 * Direktlink auf die Karte. Pro Browser-Session wegklickbar; eingerichtet verschwindet er von selbst.
 */
export function CalendarOutlookSetupBanner() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // Einrichten dürfen nur Admins (die Einrichtungs-Routen verlangen ADMIN).
  const canSetup = user?.role === 'ADMIN';
  const [dismissed, setDismissed] = useState(readDismissed);

  const { data } = useQuery({
    // Unter 'calendar-sync-status': die Karte frischt nach der Einrichtung alles darunter auf.
    queryKey: ['calendar-sync-status', 'setup'],
    queryFn: () => api.getCalendarSyncSetupState(),
    enabled: canSetup && !dismissed,
    staleTime: 60_000,
    retry: false,
  });

  if (!canSetup || dismissed || !data || data.permissionGranted) return null;

  return (
    <Alert
      color="orange"
      radius="md"
      icon={<IconCalendarEvent size={18} />}
      withCloseButton
      onClose={() => {
        try {
          sessionStorage.setItem(DISMISS_KEY, '1');
        } catch {
          // Ohne sessionStorage kommt der Hinweis beim nächsten Laden wieder — kein Grund zu scheitern.
        }
        setDismissed(true);
      }}
      title="Teamkalender: Termine landen noch nicht automatisch in Outlook"
    >
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Text size="sm" maw={640}>
          {reachSentence(data)} {missingStep(data)}
        </Text>
        <Button size="xs" color="orange" onClick={() => navigate(CALENDAR_SYNC_SETUP_PATH)}>
          Outlook für alle einrichten
        </Button>
      </Group>
    </Alert>
  );
}
